import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { get, isEmpty } from 'lodash-es'

import { isUnclosedSessionStatus } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/constants'
import { getAppointmentTimeslot } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { isInitialEvaluationSessionType } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { actions, mixpanelEvents, sessionNoteTypes } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { programConfig } from '../../../common/constants/appConstants'
import { CLIENT_NEW_NOTE, CLIENT_NOTE } from '../../../common/constants/routingConstants'
import { logToSentry, resolveDAEngageProgramName } from '../../../common/utils/utils'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../data/store'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { useLazyGetTransfersQuery } from '../../../payments/data/transfersApi'
import { useAssignmentsData } from '../../assignments/data/hooks/useAssignmentsData'
import { useLazyGetContentsByProgramIdQuery } from '../../data/contentsMetadataApi'
import { setToastContent } from '../../data/ltToastAutoActions'
import { populateInitialValues } from '../ClientNotes'
import { useLazyGetNoteQuery, useLazyGetNotesQuery } from '../data/notesApi'
import { DA_FLEXIBLE_NOTE_NAME } from '../utils/noteConstants'
import { getPendingChargeNavState } from '../utils/noteNavigationUtils'
import { getNpModality, getNpVisitType, NP_SESSION_NOTE_NAME } from '../utils/npSessionNote'

type UseNoteNavigationActionsArgs = {
  appointments: Appointment[]
  episodes: Episode[]
  notes: Note[]
  originRoute?: string
  selectEpisode?: (episode: Episode) => void
}

type NoteNavigationState = {
  chargeId?: string
  needToUpdateCharge?: boolean
}

export const useNoteNavigationActions = ({
  appointments,
  episodes,
  notes,
  originRoute = 'careTimeline',
  selectEpisode,
}: UseNoteNavigationActionsArgs) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const userId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const { data: assignments = [] } = useAssignmentsData()
  const { enableDAFlexibleNote, isNPTestingEnabled } = useFlags()
  const isDAEngageProvider = useIsEngageDAProvider()
  const [fetchNote] = useLazyGetNoteQuery()
  const [fetchEpisodeNotes] = useLazyGetNotesQuery()
  const [fetchTransfers] = useLazyGetTransfersQuery()
  const [getContentsByProgramId] = useLazyGetContentsByProgramIdQuery()

  const showActionError = useCallback(() => {
    dispatch(
      setToastContent({
        id: 'useNoteNavigationActions-action-error',
        text: intl.formatMessage({
          defaultMessage: 'Unable to open this note. Please try again.',
          description: 'Error shown when a note cannot be opened',
        }),
        toastType: 'error',
      }),
    )
  }, [dispatch, intl])

  const getEpisode = useCallback(
    (episodeId?: string) => episodes.find((episode) => episode.id === episodeId),
    [episodes],
  )

  const selectItemEpisode = useCallback(
    (episode?: Episode) => {
      if (episode) {
        selectEpisode?.(episode)
      }
    },
    [selectEpisode],
  )

  const getPendingChargeState = useCallback(
    async (note: Note): Promise<NoteNavigationState> => {
      if (!isDAEngageProvider || !clientId || !note.appointment_id) {
        return {}
      }

      const { data: pendingTransfers = [] } = await fetchTransfers({
        provider_id: userId,
        patient_id: clientId,
        status: TransferStatus.PENDING_NOTE,
        showAlert: false,
      }).unwrap()

      return getPendingChargeNavState(note, appointments, pendingTransfers)
    },
    [appointments, clientId, fetchTransfers, isDAEngageProvider, userId],
  )

  const navigateToExistingNote = useCallback(
    async ({ noteId }: { noteId: string }) => {
      try {
        const note = await fetchNote(noteId).unwrap()
        const pendingChargeState = await getPendingChargeState(note)
        selectItemEpisode(getEpisode(note.episode_id))

        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: actions.CLIENT_NOTES_TAB_OPEN_NOTE,
          details: {
            noteType: sessionNoteTypes[note.note_type as keyof typeof sessionNoteTypes],
            ...(note.appointment_id && { appointmentId: note.appointment_id.toString() }),
            button: note.status === NoteStatusStates.DRAFT ? 'Finish note' : 'View',
          },
        })
        logToSentry('noteNavigation', {
          message: `Navigating to existing note from ${originRoute}`,
          noteId,
          appointmentId: note.appointment_id,
          episodeId: note.episode_id,
          clientId,
          noteType: note.note_type,
          noteStatus: note.status,
        })
        navigate(`${CLIENT_NOTE.route}?id=${noteId}`, {
          state: {
            note,
            ...pendingChargeState,
            originRoute,
          },
        })
      } catch {
        showActionError()
      }
    },
    [clientId, fetchNote, getEpisode, getPendingChargeState, navigate, originRoute, selectItemEpisode, showActionError],
  )

  const navigateToNewNote = useCallback(
    async (note: Record<string, unknown>, episode: Episode) => {
      try {
        let pendingChargeState: NoteNavigationState = {}
        if (note.appointment_id) {
          pendingChargeState = await getPendingChargeState(note as unknown as Note)
        }

        if ([NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type as NoteTypes)) {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: actions.CLIENT_NOTES_TAB_OPEN_NOTE,
            details: {
              noteType: sessionNoteTypes[note.note_type as keyof typeof sessionNoteTypes],
              ...(note.appointment_id != null ? { appointmentId: String(note.appointment_id) } : {}),
              button: 'Add note',
            },
          })
        }

        selectItemEpisode(episode)
        navigate(CLIENT_NEW_NOTE.route, {
          state: {
            note,
            ...pendingChargeState,
            originRoute,
          },
        })
      } catch {
        showActionError()
      }
    },
    [getPendingChargeState, navigate, originRoute, selectItemEpisode, showActionError],
  )

  const loadContents = useCallback(
    async (episode: Episode, appointment?: Appointment): Promise<Content[] | undefined> => {
      const programId = appointment?.programId || episode.program_id
      if (!programId) {
        showActionError()
        return
      }

      try {
        return await getContentsByProgramId({ programId }).unwrap()
      } catch {
        showActionError()
        return
      }
    },
    [getContentsByProgramId, showActionError],
  )

  const getSessionInitialValues = useCallback(
    (
      contentName: string | undefined,
      oldContentName: string | undefined,
      episode: Episode,
      episodeSessions: Appointment[],
      sessionIndex: number,
      contents: Content[],
    ) => {
      if (!contentName) {
        return
      }

      const formResponses: Array<{
        contentName: string
        data: Note['body'] | string
        responseUiSchema?: unknown
      }> = []
      const episodeProgramConfig = programConfig[episode.program_name]
      const clientIntake = assignments.find(
        (assignment) =>
          [
            episodeProgramConfig?.clientIntake,
            episodeProgramConfig?.oldClientIntake,
            episodeProgramConfig?.clientIntakeNonUS,
          ].includes(assignment.content.name) && assignment.episode_id === episode.id,
      )
      if (clientIntake) {
        const clientIntakeResponse = get(clientIntake, 'assignment_responses.0.response')
        if (clientIntakeResponse) {
          formResponses.push({
            contentName: clientIntake.content.name,
            data: clientIntakeResponse,
            responseUiSchema: clientIntake.content_meta_data.uiSchema,
          })
        }
      }

      const previousAppointment = episodeSessions[sessionIndex - 1]
      const previousNote = notes.find((note) => note.appointment_id === previousAppointment?.appointmentId)
      if (previousNote) {
        formResponses.push({
          contentName: previousNote.content_meta_data.name,
          data: previousNote.body,
          responseUiSchema: previousNote.content_meta_data.meta_data.uiSchema,
        })
      }

      const initialValues = populateInitialValues(formResponses, contentName, contents)
      if (!isEmpty(initialValues)) {
        return initialValues
      }
      return oldContentName ? populateInitialValues(formResponses, oldContentName, contents) : undefined
    },
    [assignments, notes],
  )

  const addSessionNote = useCallback(
    async ({ appointmentId, episodeId }: { appointmentId: number; episodeId: string }) => {
      const appointment = appointments.find((session) => session.appointmentId === appointmentId)
      const episode = getEpisode(episodeId)
      if (!appointment || !episode) {
        showActionError()
        return
      }
      //Re-read the episode's notes (bypassing the cache) and resume instead of duplicating.
      try {
        const freshNotes = await fetchEpisodeNotes({ episodeId }, false).unwrap()
        const existingNote = freshNotes.find(
          (note) => note.appointment_id === appointmentId && note.status !== NoteStatusStates.COMPLETED,
        )
        if (existingNote) {
          await navigateToExistingNote({ noteId: existingNote.id })
          return
        }
      } catch (error) {
        // A failed lookup shouldn't block note creation; the API still guards against duplicates.
        logToSentry('noteNavigation', {
          message: 'Failed to fetch episode notes before creating new note',
          error,
        })
      }

      const contents = await loadContents(episode, appointment)
      if (!contents) {
        return
      }

      const episodeSessions = appointments.filter(
        (session) =>
          session.episodeId === episode.id &&
          ['rescheduled', 'newAppt', 'concluded', 'completed'].includes(session.appointmentStatus),
      )
      const sessionIndex = episodeSessions.findIndex(({ appointmentId }) => appointmentId === appointment.appointmentId)
      const isInitialEvaluation =
        isInitialEvaluationSessionType(
          appointment.suggestedSessionType,
          resolveDAEngageProgramName(episode.program_name, isDAEngageProvider),
        ) ?? sessionIndex === 0
      const { INITIAL_NOTE, SESSION_NOTE, OLD_INITIAL_NOTE, OLD_SESSION_NOTE } =
        Programs.getProgramConfigByName(episode.program_name)?.noteContentMetadataName ?? {}

      const isNpSessionNote = !!isNPTestingEnabled && isNeuropsychEvaluationProgram(episode.program_name)
      const npVisitType = isNpSessionNote ? getNpVisitType(appointment) : undefined

      let contentName: string | undefined
      if (isNpSessionNote) {
        contentName = NP_SESSION_NOTE_NAME
      } else if (
        enableDAFlexibleNote &&
        isDAEngageProvider &&
        isUnclosedSessionStatus(appointment.appointmentStatus) &&
        !appointment.isChargeReversed
      ) {
        contentName = DA_FLEXIBLE_NOTE_NAME
      } else if (episode.program_name === ProgramNames.AlcoholUseDisorderTherapy) {
        contentName = ['aud', 'sud', 'aud_and_sud'].includes(episode.assessment_set)
          ? isInitialEvaluation
            ? INITIAL_NOTE
            : SESSION_NOTE
          : isInitialEvaluation
            ? OLD_INITIAL_NOTE
            : OLD_SESSION_NOTE
      } else {
        contentName = isInitialEvaluation ? INITIAL_NOTE : SESSION_NOTE
      }

      const oldContentName = isInitialEvaluation ? OLD_INITIAL_NOTE : OLD_SESSION_NOTE
      const contentMetadata =
        contents.find((content) => content.name === contentName) ??
        contents.find((content) => content.name === OLD_INITIAL_NOTE || content.name === OLD_SESSION_NOTE)
      if (!contentMetadata) {
        showActionError()
        return
      }

      const modality = isNpSessionNote ? getNpModality(appointment.meetingFormat) : undefined
      const body = isNpSessionNote
        ? { ...(npVisitType && { sessionType: npVisitType }), ...(modality && { modality }) }
        : getSessionInitialValues(contentName, oldContentName, episode, episodeSessions, sessionIndex, contents)
      const title = isDAEngageProvider
        ? intl.formatDate(new Date(`${appointment.startDate}T00:00:00`), {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          })
        : intl.formatMessage(
            {
              defaultMessage: 'Session {sessionNumber} Note',
              description: 'Title for a new session note',
            },
            { sessionNumber: sessionIndex + 1 },
          )

      await navigateToNewNote(
        {
          title,
          appointment_id: appointment.appointmentId,
          content_meta_data: contentMetadata,
          note_type: isInitialEvaluation && !isNpSessionNote ? NoteTypes.INITIAL : NoteTypes.SESSION,
          sessionDate: appointment.startDate,
          sessionNumber: appointment.sessionNumber,
          appointmentTimeslot: getAppointmentTimeslot({ appointment, intl }),
          body,
        },
        episode,
      )
    },
    [
      appointments,
      enableDAFlexibleNote,
      fetchEpisodeNotes,
      getEpisode,
      getSessionInitialValues,
      intl,
      isDAEngageProvider,
      isNPTestingEnabled,
      loadContents,
      navigateToExistingNote,
      navigateToNewNote,
      showActionError,
    ],
  )

  return {
    addSessionNote,
    navigateToExistingNote,
    navigateToNewNote,
    loadContents,
    getEpisode,
    selectItemEpisode,
    showActionError,
  }
}
